import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/auth/RequirePermission';
import { AccountSwitcher } from '@/components/shell/AccountSwitcher';
import { UserMenu } from '@/components/shell/UserMenu';
import { CompanyBrand } from '@/components/shell/CompanyBrand';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { LegalAcceptanceScreen, LegalUpdateBanner } from '@/components/legal/LegalGate';
import { isWideRoute, navSections } from './navigation';

/**
 * The application shell (M0-24).
 *
 * Responsive from the start, not adapted later: a technician standing at a
 * water heater with a phone is a first-class case until the mobile app ships
 * (M3), so the small screen gets a real navigation drawer rather than a
 * squeezed sidebar. The same markup serves both — the sidebar *is* the drawer,
 * positioned differently.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const hasPermission = useHasPermission();
  // M2-48: the SaaS terms. `legal` is null when this environment has the gate
  // off — and then nothing below renders (components/legal/LegalGate.tsx).
  const legal = useCurrentUser().data?.legal ?? null;
  const [reviewingLegal, setReviewingLegal] = useState(false);

  // Navigating closes the drawer. Without this, following a link on a phone
  // leaves the menu covering the screen you just asked for.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  if (legal?.state === 'Required') {
    return <LegalAcceptanceScreen mode="required" />;
  }
  if (legal?.state === 'DueSoon' && reviewingLegal) {
    return <LegalAcceptanceScreen mode="review" onClose={() => setReviewingLegal(false)} />;
  }

  const sections = navSections.filter(
    (section) => !section.permission || hasPermission(section.permission),
  );

  const navigation = (
    <nav className="flex flex-col gap-0.5 p-3" aria-label={t('appLayout.mainNav')}>
      {sections.map((section) => (
        <NavLink
          key={section.to}
          to={section.to}
          end={section.end}
          className={({ isActive }) =>
            cn(
              // `tap-row`, not `tap-target`: this is already a flex container
              // and only needs the 44px floor on a touch screen (M1-22). The
              // drawer is how a technician reaches their jobs on a phone, so
              // its rows are part of that flow whatever else M2-24 still owes.
              'tap-row flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors',
              isActive
                ? 'bg-[var(--color-primary)] font-medium text-[var(--color-primary-foreground)]'
                : 'text-[var(--color-foreground)] hover:bg-[var(--color-muted)]',
            )
          }
        >
          <span>{t(section.labelKey)}</span>
          {section.milestone ? (
            <span
              className="rounded-full border px-1.5 py-0.5 text-[0.625rem] opacity-70"
              title={t('appLayout.builtIn', { milestone: section.milestone })}
            >
              {section.milestone}
            </span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[16rem_1fr]">
      {/* Backdrop only exists on small screens, where the sidebar overlays. */}
      {drawerOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          aria-label={t('appLayout.closeMenu')}
          onClick={() => setDrawerOpen(false)}
        />
      ) : null}

      <aside
        id="app-navigation"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r bg-[var(--color-card)] transition-transform duration-200',
          'lg:static lg:z-auto',
          // max-lg, not a base class with an lg: override: both set the same
          // `translate` property in Tailwind v4, and the base one won — which
          // parked the desktop sidebar off-screen at translate: -100%. Scoping
          // the off-canvas position to small screens removes the conflict
          // instead of trying to out-specify it.
          drawerOpen ? 'translate-x-0' : 'max-lg:-translate-x-full',
        )}
        aria-hidden={undefined}
      >
        <div className="flex h-14 items-center border-b px-4">
          <span className="font-semibold text-[var(--color-primary)]">MainLine Pro</span>
        </div>
        <div className="px-3 pt-3">
          <AccountSwitcher />
        </div>
        <div className="flex-1 overflow-y-auto">{navigation}</div>
      </aside>

      {/* min-w-0: a grid item's default `min-width: auto` refuses to shrink
          below its content, so a wide child — the dispatch board — pushed this
          column past the viewport and scrolled the whole PAGE sideways instead
          of scrolling inside its own container. Caught by measuring in the
          browser; jsdom reports every width as zero and cannot see it. */}
      <div className="flex min-h-screen min-w-0 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-[var(--color-background)] px-4">
          <button
            type="button"
            className="rounded-md p-2 hover:bg-[var(--color-muted)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none lg:hidden"
            aria-label={t('appLayout.openMenu')}
            aria-expanded={drawerOpen}
            aria-controls="app-navigation"
            onClick={() => setDrawerOpen(true)}
          >
            <span aria-hidden="true" className="block space-y-1">
              <span className="block h-0.5 w-5 bg-current" />
              <span className="block h-0.5 w-5 bg-current" />
              <span className="block h-0.5 w-5 bg-current" />
            </span>
          </button>

          {/* M2-19: the company's own logo and name, at every size — the
              product name stays in the sidebar. */}
          <div className="min-w-0">
            <CompanyBrand />
          </div>

          <div className="ml-auto">
            <UserMenu />
          </div>
        </header>

        <main
          className={cn(
            'mx-auto w-full flex-1 px-4 py-6 sm:py-8',
            isWideRoute(location.pathname) ? 'max-w-none' : 'max-w-5xl',
          )}
        >
          {legal?.state === 'DueSoon' ? (
            <LegalUpdateBanner status={legal} onReview={() => setReviewingLegal(true)} />
          ) : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
